import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { LEGAL_UPDATED_AT } from "../lib/legalMeta";
import LegalInfo from "../components/LegalInfo";

const BG = "https://images.unsplash.com/photo-1644896916261-655117008649";

export default function Register() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [role, setRole] = useState("buyer");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      setErr(t("register.must_accept"));
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await register(name, email, password, true, LEGAL_UPDATED_AT, role);
      nav(role === "producer" ? "/producer" : "/kyc");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 lg:p-16 bg-bg order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_credit-marketplace-11/artifacts/xeso8axu_image.png"
              alt="GreenBelt"
              className="h-10 w-10 object-cover rounded-sm"
            />
            <span className="font-serif text-2xl">GreenBelt</span>
          </Link>
          <h1 className="font-serif text-4xl mt-10 tracking-tight">Open an account</h1>
          <p className="text-sm text-ink2 mt-2">Start trading verified UCS credits in minutes.</p>

          <form onSubmit={submit} className="mt-10 space-y-4">
            <div>
              <label className="label-tag block mb-2">{t("register.signup_as")}</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setRole("buyer")}
                  data-testid="register-role-buyer"
                  className={`px-3 py-3 text-left border transition-colors ${
                    role === "buyer" ? "bg-ink text-white border-ink" : "border-line text-ink hover:border-ink"
                  }`}
                >
                  <div className="text-[11px] font-mono uppercase tracking-wider">{t("register.role_buyer")}</div>
                  <div className="text-[10px] opacity-70 mt-1">{t("register.role_buyer_hint")}</div>
                </button>
                <button
                  type="button"
                  onClick={() => setRole("producer")}
                  data-testid="register-role-producer"
                  className={`px-3 py-3 text-left border transition-colors ${
                    role === "producer" ? "bg-ink text-white border-ink" : "border-line text-ink hover:border-ink"
                  }`}
                >
                  <div className="text-[11px] font-mono uppercase tracking-wider">{t("register.role_producer")}</div>
                  <div className="text-[10px] opacity-70 mt-1">{t("register.role_producer_hint")}</div>
                </button>
              </div>
            </div>
            <div>
              <label className="label-tag block mb-2">Full name</label>
              <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className="input-field" />
            </div>
            <div>
              <label className="label-tag block mb-2">Email</label>
              <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" />
            </div>
            <div>
              <label className="label-tag block mb-2">Password</label>
              <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input-field" />
              <div className="text-[11px] text-ink2 mt-1">Minimum 6 characters.</div>
            </div>

            <label className="flex items-start gap-3 text-xs text-ink2 leading-relaxed cursor-pointer pt-2">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="register-accept-terms"
                className="mt-0.5 h-4 w-4 accent-ink shrink-0"
                required
              />
              <span>
                <Trans
                  i18nKey="register.accept_terms"
                  components={{
                    terms: <LegalInfo ns="legal_terms" testid="register-terms" />,
                    privacy: <LegalInfo ns="legal_privacy" testid="register-privacy" />,
                    disclosures: <LegalInfo ns="legal_disclosures" testid="register-disclosures" />,
                  }}
                />
                <span className="block text-[10px] font-mono text-ink2/80 mt-1">v{LEGAL_UPDATED_AT}</span>
              </span>
            </label>

            {err && <div className="text-sm text-danger" data-testid="register-error">{err}</div>}
            <button data-testid="register-submit" type="submit" disabled={busy || !accepted} className="btn-primary w-full disabled:opacity-50">
              {busy ? "Creating…" : "Create account →"}
            </button>
          </form>
          <p className="mt-8 text-sm text-ink2">Already a member? <Link to="/login" className="text-ink underline">Sign in</Link>.</p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={BG} alt="forest" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tl from-ink/40 via-transparent to-transparent" />
      </div>
    </div>
  );
}
